import { createSlice } from '@reduxjs/toolkit';
import { UiState } from 'constants/ui';
import { GenerateLabelModal } from 'constants/conversation';
import { HttpStatus } from 'data/api/response-codes';
import { stateName } from './constants';
export const initialState = {
  conversation: undefined,
  tracking: undefined,
  shipmentInstructions: undefined,
  shippingInstructions: undefined,
  recipientId: null,
  digitalLabel: undefined,
  activeModal: GenerateLabelModal.None,
  ui: {
    isSidebarOpen: false,
    sendMessage: {
      error: null,
      uiState: UiState.Idle
    },
    enterTrackingCode: {
      error: null,
      uiState: UiState.Idle
    },
    markAsShipped: {
      error: null,
      uiState: UiState.Idle
    },
    createOffer: {
      error: null,
      uiState: UiState.Idle
    },
    getTransaction: {
      error: null,
      uiState: UiState.Idle
    },
    acceptOffer: {
      error: null,
      uiState: UiState.Idle
    },
    rejectOffer: {
      error: null,
      uiState: UiState.Idle
    },
    offerRequestOptions: {
      error: null,
      uiState: UiState.Idle
    },
    getConversation: {
      error: null,
      uiState: UiState.Idle,
      status: null
    },
    selectDropOffType: {
      selectedDropOffTypeId: null,
      isSubmitEnabled: false,
      uiState: UiState.Idle,
      error: null
    },
    harassmentWarning: {
      isModalOpen: false,
      wasModalOpen: false,
      scoredMessage: null,
      scoredHarassmentMessageCorrelationId: null
    },
    getCourierPickUpDates: {
      selectedCourierPickUpDate: undefined,
      selectedCourierPickUpTime: undefined,
      error: null,
      uiState: UiState.Idle
    },
    selectLabelType: {
      selectedLabelType: null,
      isSubmitEnabled: false
    },
    selectedShippingDate: null,
    isEmailSharingModalVisible: false,
    shippingInstructions: {
      error: null,
      uiState: UiState.Idle
    },
    trackingJourneySummary: {
      error: null,
      uiState: UiState.Idle
    }
  },
  transaction: null
};

const getConversationSuccess = (draft, action) => {
  const {
    conversation,
    status
  } = action.payload;
  draft.conversation = conversation;
  draft.ui.getConversation.uiState = UiState.Success;
  draft.ui.getConversation.status = status;
};

const getConversationUiPending = (draft, action) => {
  draft.ui.getConversation.uiState = UiState.Pending;
  draft.ui.getConversation.status = null;

  if (!action.payload.fromPostReply) {
    draft.conversation = undefined;
  }
};

const getConversationUiFailure = (draft, action) => {
  const {
    error,
    status
  } = action.payload;
  draft.conversation = undefined;
  draft.ui.getConversation.error = error;
  draft.ui.getConversation.uiState = UiState.Failure;
  draft.ui.getConversation.status = status;
};

const clearConversation = draft => {
  draft.conversation = undefined;
  draft.ui.getConversation.status = null;
  draft.ui.getConversation.uiState = UiState.Idle;
  draft.ui.getConversation.error = null;
};

const setConversation = (draft, action) => {
  const {
    conversation
  } = action.payload;
  draft.conversation = conversation;
  draft.ui.getConversation.status = HttpStatus.Ok;
  draft.ui.getConversation.uiState = UiState.Success;
  draft.ui.getConversation.error = null;
};

const getComplaintSuccess = (draft, action) => {
  if (draft.conversation) {
    draft.conversation.complaintResolution = action.payload;
  }
};

const setIsSidebarOpen = (draft, action) => {
  draft.ui.isSidebarOpen = action.payload.isOpen;
};

const getTrackingJourneySummaryRequest = draft => {
  draft.ui.trackingJourneySummary.uiState = UiState.Pending;
};

const getTrackingJourneySummarySuccess = (draft, action) => {
  draft.ui.trackingJourneySummary.uiState = UiState.Success;
  draft.ui.enterTrackingCode.error = null;
  draft.tracking = action.payload;
};

const getTrackingJourneySummaryFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.trackingJourneySummary.uiState = UiState.Failure;
  draft.ui.trackingJourneySummary.error = error;
};

const getShipmentInstructionsSuccess = (draft, action) => {
  draft.shipmentInstructions = action.payload;
};

const getReturnShipmentOptionSuccess = (draft, action) => {
  draft.returnShippingOption = action.payload;
};

const postReplyRequest = draft => {
  draft.ui.sendMessage.uiState = UiState.Pending;
};

const postReplySuccess = draft => {
  draft.ui.sendMessage.uiState = UiState.Success;
  draft.ui.sendMessage.error = null;
};

const setSendMessageUiFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.sendMessage.error = error;
  draft.ui.sendMessage.uiState = UiState.Failure;
};

const createItemConversationThreadRequest = draft => {
  draft.ui.getConversation.uiState = UiState.Pending;
};

const createItemConversationThreadFailure = (draft, action) => {
  draft.ui.getConversation.uiState = UiState.Failure;
  draft.ui.getConversation.error = action.payload.error;
};

const createItemConversationThreadSuccess = (draft, actions) => {
  draft.conversation = actions.payload.conversation;
  draft.ui.getConversation.uiState = UiState.Success;
};

const createConversationThreadRequest = draft => {
  draft.ui.sendMessage.uiState = UiState.Pending;
};

const enterTrackingCodeRequest = draft => {
  draft.ui.enterTrackingCode.uiState = UiState.Pending;
};

const enterTrackingCodeSuccess = draft => {
  draft.ui.enterTrackingCode.uiState = UiState.Success;
  draft.ui.enterTrackingCode.error = null;
};

const enterTrackingCodeUiFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.enterTrackingCode.uiState = UiState.Failure;
  draft.ui.enterTrackingCode.error = error;
};

const deleteEnterTrackingCodeError = draft => {
  draft.ui.enterTrackingCode.error = null;
};

const markAsShippedRequest = draft => {
  draft.ui.markAsShipped.uiState = UiState.Pending;
};

const markAsShippedSuccess = draft => {
  draft.ui.markAsShipped.uiState = UiState.Success;
  draft.ui.markAsShipped.error = null;
};

const markAsShippedUiFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.markAsShipped.uiState = UiState.Failure;
  draft.ui.markAsShipped.error = error;
};

const setRecipientId = (draft, action) => {
  draft.recipientId = action.payload.recipientId;
};

const setSelectedDropOffTypeMethodRequest = draft => {
  draft.ui.selectDropOffType.uiState = UiState.Pending;
};

const setSelectedDropOffTypeMethodFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.selectDropOffType.uiState = UiState.Failure;
  draft.ui.selectDropOffType.error = error;
};

const setSelectedDropOffTypeMethodSuccess = draft => {
  draft.ui.selectDropOffType.uiState = UiState.Success;
  draft.ui.selectDropOffType.error = null;
};

const setSelectedDropOffTypeId = (draft, action) => {
  draft.ui.selectDropOffType.selectedDropOffTypeId = action.payload.dropOffTypeId;
  draft.ui.selectDropOffType.isSubmitEnabled = true;
};

const clearDropOffTypeSelection = draft => {
  draft.ui.selectDropOffType.isSubmitEnabled = false;
  draft.ui.selectDropOffType.selectedDropOffTypeId = null;
};

const deleteDropOffTypeSelectionError = draft => {
  draft.ui.selectDropOffType.error = null;
};

const getDigitalLabelSuccess = (draft, action) => {
  draft.digitalLabel = action.payload;
};

const createOfferRequest = draft => {
  draft.ui.createOffer.uiState = UiState.Pending;
};

const createOfferSuccess = draft => {
  draft.ui.createOffer.uiState = UiState.Success;
  draft.ui.createOffer.error = null;
};

const createOfferFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.createOffer.uiState = UiState.Failure;
  draft.ui.createOffer.error = error;
};

const acceptOfferRequest = draft => {
  draft.ui.acceptOffer.uiState = UiState.Pending;
};

const acceptOfferSuccess = draft => {
  draft.ui.acceptOffer.uiState = UiState.Success;
  draft.ui.acceptOffer.error = null;
};

const acceptOfferFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.acceptOffer.uiState = UiState.Failure;
  draft.ui.acceptOffer.error = error;
};

const rejectOfferRequest = draft => {
  draft.ui.rejectOffer.uiState = UiState.Pending;
};

const rejectOfferSuccess = draft => {
  draft.ui.rejectOffer.uiState = UiState.Success;
  draft.ui.rejectOffer.error = null;
};

const rejectOfferFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.rejectOffer.uiState = UiState.Failure;
  draft.ui.rejectOffer.error = error;
};

const setMessageDeleted = (draft, action) => {
  var _draft$conversation;

  const {
    messageId,
    photoId
  } = action.payload;
  const entity = (_draft$conversation = draft.conversation) === null || _draft$conversation === void 0 ? void 0 : _draft$conversation.messages.find(_ref => {
    let {
      message
    } = _ref;
    return message.id === messageId;
  });
  const message = entity === null || entity === void 0 ? void 0 : entity.message;
  if (!message) return;

  if (photoId) {
    const photo = message.photos.find(_ref2 => {
      let {
        id
      } = _ref2;
      return id === photoId;
    });
    if (!photo) return;
    photo.url = '';
    photo.isHidden = true;
  } else {
    message.isHidden = true;
    message.content = '';
  }
};

const setMessageLiked = (draft, action) => {
  var _draft$conversation2;

  const {
    messageId,
    photoId,
    reaction
  } = action.payload;
  const entity = (_draft$conversation2 = draft.conversation) === null || _draft$conversation2 === void 0 ? void 0 : _draft$conversation2.messages.find(_ref3 => {
    let {
      message
    } = _ref3;
    return message && 'id' in message && message.id === messageId;
  });
  const message = entity === null || entity === void 0 ? void 0 : entity.message;
  if (!message) return;

  if (photoId) {
    const photo = message.photos.find(_ref4 => {
      let {
        id
      } = _ref4;
      return id === photoId;
    });
    if (!photo) return;
    photo.reaction = {
      id: reaction.id,
      type: reaction.type
    };
    return;
  }

  message.reaction = {
    id: reaction.id,
    type: reaction.type
  };
};

const setMessageUnliked = (draft, action) => {
  var _draft$conversation3;

  const {
    messageId,
    photoId
  } = action.payload;
  const entity = (_draft$conversation3 = draft.conversation) === null || _draft$conversation3 === void 0 ? void 0 : _draft$conversation3.messages.find(_ref5 => {
    let {
      message
    } = _ref5;
    return message && 'id' in message && message.id === messageId;
  });
  const message = entity === null || entity === void 0 ? void 0 : entity.message;
  if (!message) return;

  if (photoId) {
    const photo = message.photos.find(_ref6 => {
      let {
        id
      } = _ref6;
      return id === photoId;
    });
    if (!photo) return;
    photo.reaction = null;
    return;
  }

  message.reaction = null;
};

const setActiveGenerateLabelModal = (draft, action) => {
  const {
    modal
  } = action.payload;
  draft.activeModal = modal;
};

const setHarassmentMessage = (draft, action) => {
  const {
    messageText,
    correlationId
  } = action.payload;
  draft.ui.sendMessage.uiState = UiState.Failure;
  draft.ui.harassmentWarning.isModalOpen = true;
  draft.ui.harassmentWarning.wasModalOpen = true;
  draft.ui.harassmentWarning.scoredMessage = messageText;
  draft.ui.harassmentWarning.scoredHarassmentMessageCorrelationId = correlationId;
};

const clearHarassmentMessage = draft => {
  draft.ui.harassmentWarning.isModalOpen = false;
  draft.ui.harassmentWarning.wasModalOpen = false;
  draft.ui.harassmentWarning.scoredMessage = null;
};

const setHarassmentWarningModalClosed = draft => {
  draft.ui.harassmentWarning.isModalOpen = false;
};

const setEmailSharingModalVisible = (draft, action) => {
  const {
    isVisible
  } = action.payload;
  draft.ui.isEmailSharingModalVisible = isVisible;
};

const getCourierPickUpDatesRequest = draft => {
  draft.ui.getCourierPickUpDates.uiState = UiState.Pending;
};

const getCourierPickUpDatesFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.getCourierPickUpDates.uiState = UiState.Failure;
  draft.ui.getCourierPickUpDates.error = error;
};

const getCourierPickUpDatesSuccess = (draft, action) => {
  var _firstAvailablePickUp;

  const firstAvailablePickUpDate = action.payload.find(courierPickUpDate => courierPickUpDate.isAvailable);
  draft.ui.getCourierPickUpDates.uiState = UiState.Success;
  draft.ui.getCourierPickUpDates.error = null;
  draft.courierPickupDates = action.payload;
  draft.ui.getCourierPickUpDates.selectedCourierPickUpDate = firstAvailablePickUpDate;
  draft.ui.getCourierPickUpDates.selectedCourierPickUpTime = firstAvailablePickUpDate === null || firstAvailablePickUpDate === void 0 ? void 0 : (_firstAvailablePickUp = firstAvailablePickUpDate.times) === null || _firstAvailablePickUp === void 0 ? void 0 : _firstAvailablePickUp.find(availableTime => availableTime.isAvailable);
};

const setSelectedCourierPickUpDate = (draft, action) => {
  var _action$payload$couri;

  draft.ui.getCourierPickUpDates.selectedCourierPickUpDate = action.payload.courierPickUpDate;
  draft.ui.getCourierPickUpDates.selectedCourierPickUpTime = (_action$payload$couri = action.payload.courierPickUpDate.times) === null || _action$payload$couri === void 0 ? void 0 : _action$payload$couri.find(availableTime => availableTime.isAvailable);
};

const setSelectedCourierPickUpTime = (draft, action) => {
  draft.ui.getCourierPickUpDates.selectedCourierPickUpTime = action.payload.courierPickUpTime;
};

const clearCourierPickUpDatesSelection = (draft, action) => {
  var _firstAvailablePickUp2;

  const firstAvailablePickUpDate = action.payload.find(courierPickUpDate => courierPickUpDate.isAvailable);
  draft.ui.getCourierPickUpDates.selectedCourierPickUpDate = firstAvailablePickUpDate;
  draft.ui.getCourierPickUpDates.selectedCourierPickUpTime = firstAvailablePickUpDate === null || firstAvailablePickUpDate === void 0 ? void 0 : (_firstAvailablePickUp2 = firstAvailablePickUpDate.times) === null || _firstAvailablePickUp2 === void 0 ? void 0 : _firstAvailablePickUp2.find(availableTime => availableTime.isAvailable);
  if (!draft.shipmentInstructions) return;
  draft.shipmentInstructions.selectedCourierPickUpDate = undefined;
  draft.shipmentInstructions.selectedCourierPickUpTime = undefined;
};

const confirmCourierPickUpDateSelection = (draft, action) => {
  if (!draft.shipmentInstructions) return;
  draft.shipmentInstructions.selectedCourierPickUpDate = action.payload.courierPickUpDate;
  draft.shipmentInstructions.selectedCourierPickUpTime = action.payload.courierPickUpTime;
};

const setSelectedLabelType = (draft, action) => {
  draft.ui.selectLabelType.selectedLabelType = action.payload.labelType;
  draft.ui.selectLabelType.isSubmitEnabled = true;
};

const clearDigitalLabelTypeSelection = draft => {
  draft.ui.selectLabelType.isSubmitEnabled = false;
  draft.ui.selectLabelType.selectedLabelType = null;
};

const getOfferRequestOptionsRequest = draft => {
  draft.ui.offerRequestOptions.uiState = UiState.Pending;
};

const getOfferRequestOptionsSuccess = (draft, action) => {
  draft.offerRequestOptions = action.payload.offerRequestOptions;
  draft.ui.offerRequestOptions.uiState = UiState.Success;
  draft.ui.offerRequestOptions.error = null;
};

const getOfferRequestOptionsFailure = (draft, action) => {
  draft.ui.offerRequestOptions.uiState = UiState.Failure;
  draft.ui.offerRequestOptions.error = action.payload.error;
};

const setSelectedShippingDate = (draft, action) => {
  const {
    shippingDate
  } = action.payload;
  draft.ui.selectedShippingDate = shippingDate;
};

const fetchTransactionRequest = draft => {
  draft.ui.getTransaction.uiState = UiState.Pending;
};

const fetchTransactionRequestSuccess = (draft, action) => {
  const {
    transaction
  } = action.payload;
  draft.transaction = transaction;
  draft.ui.getTransaction.uiState = UiState.Success;
  draft.ui.getTransaction.error = null;
};

const fetchTransactionRequestFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.getTransaction.uiState = UiState.Failure;
  draft.ui.getTransaction.error = error;
};

const fetchShippingInstructionsRequest = draft => {
  draft.ui.shippingInstructions.uiState = UiState.Pending;
};

const fetchShippingInstructionsRequestSuccess = (draft, action) => {
  const {
    shippingInstructions
  } = action.payload;
  draft.shippingInstructions = shippingInstructions;
  draft.ui.shippingInstructions.uiState = UiState.Success;
  draft.ui.shippingInstructions.error = null;
};

const fetchShippingInstructionsRequestFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.shippingInstructions.uiState = UiState.Failure;
  draft.ui.shippingInstructions.error = error;
};

const conversationSlice = createSlice({
  name: stateName,
  initialState,
  reducers: {
    getConversationSuccess,
    getConversationUiFailure,
    getConversationUiPending,
    clearConversation,
    setConversation,
    getComplaintSuccess,
    setIsSidebarOpen,
    getTrackingJourneySummarySuccess,
    getTrackingJourneySummaryRequest,
    getTrackingJourneySummaryFailure,
    getShipmentInstructionsSuccess,
    postReplyRequest,
    postReplyFailure: setSendMessageUiFailure,
    postReplySuccess,
    setRecipientId,
    createConversationThreadRequest,
    createConversationThreadFailure: setSendMessageUiFailure,
    enterTrackingCodeRequest,
    enterTrackingCodeSuccess,
    enterTrackingCodeUiFailure,
    deleteEnterTrackingCodeError,
    markAsShippedRequest,
    markAsShippedSuccess,
    markAsShippedUiFailure,
    setSelectedDropOffTypeId,
    clearDropOffTypeSelection,
    setSelectedDropOffTypeMethodRequest,
    setSelectedDropOffTypeMethodFailure,
    setSelectedDropOffTypeMethodSuccess,
    deleteDropOffTypeSelectionError,
    getDigitalLabelSuccess,
    createOfferRequest,
    createOfferSuccess,
    createOfferFailure,
    acceptOfferRequest,
    acceptOfferSuccess,
    acceptOfferFailure,
    rejectOfferRequest,
    rejectOfferSuccess,
    rejectOfferFailure,
    setMessageDeleted,
    setMessageLiked,
    setMessageUnliked,
    setActiveGenerateLabelModal,
    setHarassmentMessage,
    clearHarassmentMessage,
    setHarassmentWarningModalClosed,
    setEmailSharingModalVisible,
    getCourierPickUpDatesRequest,
    getCourierPickUpDatesFailure,
    getCourierPickUpDatesSuccess,
    setSelectedCourierPickUpDate,
    setSelectedCourierPickUpTime,
    confirmCourierPickUpDateSelection,
    clearCourierPickUpDatesSelection,
    setSelectedLabelType,
    clearDigitalLabelTypeSelection,
    getOfferRequestOptionsRequest,
    getOfferRequestOptionsSuccess,
    getOfferRequestOptionsFailure,
    createItemConversationThreadRequest,
    createItemConversationThreadSuccess,
    createItemConversationThreadFailure,
    getReturnShipmentOptionSuccess,
    setSelectedShippingDate,
    fetchTransactionRequest,
    fetchTransactionRequestSuccess,
    fetchTransactionRequestFailure,
    fetchShippingInstructionsRequest,
    fetchShippingInstructionsRequestSuccess,
    fetchShippingInstructionsRequestFailure
  }
});
export const {
  actions
} = conversationSlice;
export const plug = {
  [stateName]: conversationSlice.reducer
};
export default conversationSlice.reducer;